import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 31. ACTIVITY TIMELINE
// Timeline centrale : permet de voir toutes les interactions liées
// à une entité (client, entreprise, commande, réservation, projet…).
// Réutilisée par CRM, Finance, Support et AI.
//   Customer
//   ├── 02/10 Rendez-vous
//   ├── 03/10 Devis envoyé
//   ├── 05/10 Facture
//   ├── 07/10 Paiement
//   └── 10/10 Message
// ─────────────────────────────────────────────────────────────

export const TIMELINE_ENTITY_TYPES: { key: string; label: string; icon: string }[] = [
  { key: 'customer', label: 'Client', icon: 'ri-user-star-line' },
  { key: 'business', label: 'Entreprise', icon: 'ri-building-2-line' },
  { key: 'order', label: 'Commande', icon: 'ri-shopping-bag-3-line' },
  { key: 'booking', label: 'Réservation', icon: 'ri-calendar-check-line' },
  { key: 'appointment', label: 'Rendez-vous', icon: 'ri-calendar-line' },
  { key: 'invoice', label: 'Facture', icon: 'ri-file-list-3-line' },
  { key: 'project', label: 'Projet', icon: 'ri-folders-line' },
  { key: 'professional', label: 'Professionnel', icon: 'ri-user-2-line' },
  { key: 'ticket', label: 'Ticket', icon: 'ri-coupon-3-line' },
  { key: 'ai_chat', label: 'Assistant IA', icon: 'ri-robot-2-line' },
];

export function timelineEntityMeta(key: string | null | undefined) {
  return TIMELINE_ENTITY_TYPES.find((e) => e.key === key) || null;
}

export const TIMELINE_EVENT_TYPES: {
  key: string;
  label: string;
  icon: string;
  color: string;
  className: string;
}[] = [
  { key: 'created', label: 'Création', icon: 'ri-add-circle-line', color: 'secondary', className: 'bg-secondary-100 text-secondary-900' },
  { key: 'updated', label: 'Modification', icon: 'ri-edit-line', color: 'foreground', className: 'bg-background-100 text-foreground-700' },
  { key: 'appointment', label: 'Rendez-vous', icon: 'ri-calendar-check-line', color: 'primary', className: 'bg-primary-100 text-primary-900' },
  { key: 'booking', label: 'Réservation', icon: 'ri-hotel-line', color: 'primary', className: 'bg-primary-100 text-primary-900' },
  { key: 'order', label: 'Commande', icon: 'ri-shopping-bag-3-line', color: 'accent', className: 'bg-accent-100 text-accent-900' },
  { key: 'quote', label: 'Devis', icon: 'ri-file-text-line', color: 'accent', className: 'bg-accent-100 text-accent-900' },
  { key: 'invoice', label: 'Facture', icon: 'ri-file-list-3-line', color: 'accent', className: 'bg-accent-100 text-accent-900' },
  { key: 'payment', label: 'Paiement', icon: 'ri-money-dollar-circle-line', color: 'primary', className: 'bg-primary-100 text-primary-900' },
  { key: 'message', label: 'Message', icon: 'ri-chat-3-line', color: 'secondary', className: 'bg-secondary-100 text-secondary-900' },
  { key: 'note', label: 'Note', icon: 'ri-sticky-note-line', color: 'foreground', className: 'bg-background-100 text-foreground-700' },
  { key: 'status', label: 'Statut', icon: 'ri-shield-check-line', color: 'secondary', className: 'bg-secondary-100 text-secondary-900' },
  { key: 'custom', label: 'Événement', icon: 'ri-flashlight-line', color: 'foreground', className: 'bg-background-100 text-foreground-700' },
  { key: 'ai_chat', label: 'Échange IA', icon: 'ri-robot-2-line', color: 'accent', className: 'bg-accent-100 text-accent-900' },
];

export function timelineEventMeta(key: string | null | undefined) {
  return TIMELINE_EVENT_TYPES.find((e) => e.key === key) || TIMELINE_EVENT_TYPES[11];
}

export interface CoreTimelineEvent {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  entity_type: string;
  entity_id: string;
  event_type: string | null;
  title: string;
  description: string | null;
  actor_user_id: number | null;
  actor_name: string | null;
  icon: string | null;
  color: string | null;
  amount: number | null;
  currency: string | null;
  ref_table: string | null;
  ref_id: string | null;
  occurred_at: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
}

export async function listEntityTimeline(
  organizationId: number,
  entityType: string,
  entityId: string | number,
): Promise<CoreTimelineEvent[]> {
  const { data, error } = await supabase
    .from('core_activity_timeline')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('entity_type', entityType)
    .eq('entity_id', String(entityId))
    .order('occurred_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreTimelineEvent[];
}

export async function listRecentTimeline(organizationId: number, limit = 50): Promise<CoreTimelineEvent[]> {
  const { data, error } = await supabase
    .from('core_activity_timeline')
    .select('*')
    .eq('organization_id', organizationId)
    .order('occurred_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as CoreTimelineEvent[];
}

export interface TimelineEventInput {
  tenantId: number | null;
  organizationId: number;
  entityType: string;
  entityId: string | number;
  eventType?: string;
  title: string;
  description?: string | null;
  actorUserId?: number | null;
  actorName?: string | null;
  amount?: number | null;
  currency?: string | null;
  refTable?: string | null;
  refId?: string | null;
  occurredAt?: string | null;
  metadata?: Record<string, unknown> | null;
}

export async function logTimelineEvent(input: TimelineEventInput): Promise<number | null> {
  const meta = timelineEventMeta(input.eventType);
  const { data, error } = await supabase
    .from('core_activity_timeline')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      entity_type: input.entityType,
      entity_id: String(input.entityId),
      event_type: input.eventType || 'custom',
      title: input.title,
      description: input.description || null,
      actor_user_id: input.actorUserId ?? null,
      actor_name: input.actorName ?? null,
      icon: meta.icon,
      color: meta.color,
      amount: input.amount ?? null,
      currency: input.currency ?? null,
      ref_table: input.refTable ?? null,
      ref_id: input.refId ?? null,
      occurred_at: input.occurredAt || new Date().toISOString(),
      metadata: input.metadata ?? null,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function deleteTimelineEvent(id: number): Promise<void> {
  const { error } = await supabase.from('core_activity_timeline').delete().eq('id', id);
  if (error) throw error;
}

export interface TimelineStats {
  total: number;
  byEntityType: Record<string, number>;
  byEventType: Record<string, number>;
}

export async function fetchTimelineStats(organizationId: number): Promise<TimelineStats> {
  const { data } = await supabase
    .from('core_activity_timeline')
    .select('entity_type, event_type')
    .eq('organization_id', organizationId);
  const byEntityType: Record<string, number> = {};
  const byEventType: Record<string, number> = {};
  (data || []).forEach((r) => {
    const row = r as { entity_type: string; event_type: string | null };
    byEntityType[row.entity_type] = (byEntityType[row.entity_type] || 0) + 1;
    const et = row.event_type || 'custom';
    byEventType[et] = (byEventType[et] || 0) + 1;
  });
  return { total: (data || []).length, byEntityType, byEventType };
}